import { AirportData, AirportSelection, TRAVEL_CLASS_OPTIONS } from "./types";

export const formatAirportName = (airport: AirportData): string => {
  const parts = [airport.name];
  if (airport.municipality && airport.municipality !== airport.name) {
    parts.push(airport.municipality);
  }
  if (airport.iso_country) {
    parts.push(airport.iso_country);
  }
  return parts.join(", ");
};

export const createAirportSelection = (airport: AirportData, count: number = 1): AirportSelection => {
  return {
    id: airport.id,
    code: airport.iata_code || airport.gps_code || airport.local_code || "",
    name: airport.name,
    lat: airport.latitude_deg,
    lng: airport.longitude_deg,
    count,
    municipality: airport.municipality,
    type: airport.type,
    full_name: formatAirportName(airport),
  };
};

export const getFlightPairKey = (dep: AirportSelection, arr: AirportSelection): string => {
  return `${dep.code}-${arr.code}`;
};

/**
 * Generates a URL for flight search results using an opaque token in query params.
 * Pattern: /flight-estimator/results?search=[search_token]
 *
 * The search_token is an opaque identifier (UUID or random hex string) that:
 * - Does NOT expose user_id or session_id in the URL
 * - Maps to the search internally via the API
 * - Provides privacy and security
 *
 * @param searchToken - The opaque search token (UUID or random hex string)
 * @returns The URL with search query parameter
 */
export const generateFlightSearchUrl = (searchToken: string): string => {
  return `/flight-estimator/results?search=${searchToken}`;
};

export const formatTime = (minutes: number): string => {
  if (!minutes || minutes === 0) return "0h 0m";
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  return `${hours}h ${mins}m`;
};

export const computeDurationStats = (durations: number[]) => {
  if (durations.length === 0) {
    return { average: 0, median: 0, max: 0 };
  }
  const sum = durations.reduce((a, b) => a + b, 0);
  const average = Math.round(sum / durations.length);

  const sorted = [...durations].sort((a, b) => a - b);
  let median = 0;
  if (sorted.length % 2 === 1) {
    median = sorted[Math.floor(sorted.length / 2)];
  } else {
    const mid1 = sorted[sorted.length / 2 - 1];
    const mid2 = sorted[sorted.length / 2];
    median = Math.round((mid1 + mid2) / 2);
  }

  const max = sorted[sorted.length - 1];
  return { average, median, max };
};

export const formatTravelClassLabel = (travelClass: string): string => {
  return travelClass
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const travelClassOptions = TRAVEL_CLASS_OPTIONS.map((t) => ({
  value: t.name,
  label: t.label,
}));
